import type { SelectSize } from './interfaces';

// Per-size control geometry. `minHeight` floors the control height and `padX` is
// the horizontal padding. `lineHeight`/`inputHeight` keep the inner content from
// overflowing the floor (a bare `minHeight` is exceeded by the default content
// height) — mirroring how `isSlim` pairs minHeight 28 with a 16px line / 22px input.
export const sizeConfig: Record<
  SelectSize,
  { minHeight: number, padX: number, lineHeight: number, inputHeight: number | null }
> = {
  S: { minHeight: 28, padX: 8, lineHeight: 16, inputHeight: 22 },
  M: { minHeight: 36, padX: 10, lineHeight: 26, inputHeight: null },
  L: { minHeight: 56, padX: 16, lineHeight: 26, inputHeight: null },
};

export const selectTheme = (theme) => (
  {
    ...theme,
    borderRadius: 3,
    colors: {
      ...theme.colors,
      danger: '#DB1A33FF',
      neutral5: 'var(--surface-neutral-section)',
      primary25: 'var(--surface-neutral-section)',
      primary50: 'var(--surface-neutral-tile)',
      primary75: 'var(--surface-neutral-tile)',
      primary: 'var(--border-neutral-secondary)',
      neutral50: 'var(--text-neutral-placeholder)',
    },
  }
);

const getControlBorderColor = (state): string => {
  if (state.isDisabled) return 'var(--border-disabled-default)';
  return state.isFocused ? 'var(--border-primary-focused)' : 'var(--border-neutral-default)';
};

const getDropdownIndicatorBorder = (state): string => {
  if (state.isDisabled) return 'solid var(--icon-disabled-default)';
  return state.isFocused ? 'solid var(--border-neutral-secondary)' : 'solid var(--icon-neutral-secondary)';
};

export const customStyles = {
  control: (base, state) => (
    {
      ...base,
      height: 'auto',
      minHeight: 36,
      background: state.isDisabled ? 'var(--surface-disabled-default)' : 'var(--surface-neutral-tile)',
      borderWidth: 1,
      zIndex: 0,
      borderRadius: state.menuIsOpen ? '3px 3px 0 0' : 3,
      borderColor: getControlBorderColor(state),
      boxShadow: state.isFocused ? '0 1px 5px rgba(0, 0, 0, 0.1)' : null,
      outline: state.isFocused ? '1px solid var(--border-primary-focused)' : 'none',
      outlineOffset: 0,
      '&:hover': {
        borderColor: state.isDisabled ? 'var(--border-disabled-default)' : 'var(--border-neutral-secondary)',
      },
    }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--text-neutral-placeholder)',
  }),
  multiValue: (base) => ({
    ...base,
    height: 22,
    background: 'var(--surface-neutral-section)',
    borderRadius: 3,
  }),
  multiValueLabel: (base, { isDisabled }) => ({
    ...base,
    lineHeight: '16px',
    color: isDisabled ? 'var(--text-neutral-placeholder)' : 'var(--text-neutral-body)',
    fontSize: '12px',
    padding: '3px 8px',
    paddingLeft: '8px',
  }),
  multiValueRemove: (base, { isDisabled }) => ({
    ...base,
    display: isDisabled ? 'none' : 'flex',
    background: 'var(--surface-neutral-section)',
    cursor: 'pointer',
    paddingLeft: '0',
    paddingRight: '8px',
    '& svg': {
      fill: 'var(--icon-primary-default)',
    },
    '&:hover': {
      background: 'transparent',
      '& svg': {
        fill: 'var(--icon-primary-default-hover)',
      },
    },
  }),
  groupHeading: (base, state) => {
    const isFirstChild = state.data.label === state.selectProps.options[0].label;
    return {
      ...base,
      fontSize: '10px',
      paddingLeft: '10px',
      paddingBottom: '6px',
      lineHeight: '8px',
      borderTop: isFirstChild ? '' : '1px solid var(--border-neutral-table)',
      paddingTop: isFirstChild ? '10px' : '18px',
      color: 'var(--text-neutral-caption)',
      letterSpacing: '1px',
    };
  },
  group: (base) => ({
    ...base,
    paddingBottom: '0px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    alignItems: 'center',
    '> input': {
      height: 'auto',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  option: (base) => ({
    ...base,
    color: 'var(--text-neutral-default)',
    backgroundColor: 'var(--surface-neutral-tile)',
    cursor: 'pointer',
    padding: '8px 10px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: 'var(--surface-neutral-section)',
    },
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: 3,
    marginRight: 11,
    marginTop: !state.selectProps.menuIsOpen ? -4 : 4,
    border: getDropdownIndicatorBorder(state),
    borderWidth: '0 2px 2px 0',
    display: 'inline-block',
    width: 8,
    height: 8,
    transition: 'all .2s ease',
    transform: !state.selectProps.menuIsOpen ? 'rotate(45deg)' : 'rotate(-135deg)',
  }),
  menu: (base, state) => ({
    ...base,
    marginTop: 0,
    marginBottom: 0,
    backgroundColor: 'var(--surface-neutral-tile)',
    border: '1px solid var(--border-neutral-secondary)',
    borderRadius: (state.placement === 'top') ? '3px 3px 0 0' : '0 0 3px 3px',
    borderTop: (state.placement !== 'top') ? 0 : '1px solid var(--border-neutral-secondary)',
    boxShadow: '0 0 0 1px hsl(0deg 0% 0% / 10%), 0 1px 5px hsl(0deg 0% 0% / 2%)',
    zIndex: 4,
  }),
  menuList: (base) => ({
    ...base,
    textAlign: 'left',
    padding: 0,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 0 0 10px',
    whiteSpace: 'nowrap',
    alignItems: 'center',
    overflowY: 'initial',
    overflowX: 'auto',
    textAlign: 'left',
  }),
  singleValue: (base, { isDisabled }) => ({
    ...base,
    marginLeft: 0,
    marginRight: 0,
    color: isDisabled ? 'var(--text-disabled-default)' : 'var(--text-neutral-default)',
  }),
};

export const customStylesSlim = {
  control: (base, state) => (
    {
      ...base,
      minHeight: 28,
      background: state.isDisabled ? 'var(--surface-disabled-default)' : 'var(--surface-neutral-tile)',
      borderWidth: 1,
      zIndex: 1,
      borderRadius: state.menuIsOpen ? '3px 3px 0 0' : 3,
      borderColor: getControlBorderColor(state),
      boxShadow: state.isFocused ? '0 1px 5px rgba(0, 0, 0, 0.1)' : null,
      outline: state.isFocused ? '1px solid var(--border-primary-focused)' : 'none',
      outlineOffset: 0,
      '&:hover': {
        borderColor: state.isDisabled ? 'var(--border-disabled-default)' : 'var(--border-neutral-secondary)',
      },
    }),
  multiValue: (base) => ({
    ...base,
    background: 'var(--surface-neutral-section)',
    color: 'var(--text-neutral-body)',
    borderRadius: 3,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 0 0 10px',
    whiteSpace: 'nowrap',
    alignItems: 'center',
    overflowX: 'auto',
    textAlign: 'left',
  }),
  singleValue: (base, { isDisabled }) => ({
    ...base,
    marginLeft: 0,
    marginRight: 0,
    color: isDisabled ? 'var(--text-disabled-default)' : 'var(--text-neutral-default)',
  }),
  multiValueRemove: (base) => ({
    ...base,
    background: 'var(--surface-neutral-section)',
    '& svg': {
      fill: 'var(--icon-primary-default)',
    },
    '&:hover': {
      background: 'transparent',
      '& svg': {
        fill: 'var(--icon-primary-default-hover)',
      },
    },
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    height: 22,
    alignItems: 'center',
    '> input': {
      height: 'auto',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  option: (base) => ({
    ...base,
    color: 'var(--text-neutral-default)',
    backgroundColor: 'var(--surface-neutral-tile)',
    cursor: 'pointer',
    padding: '8px 10px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: 'var(--surface-neutral-section)',
    },
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: 3,
    marginRight: 11,
    marginTop: !state.selectProps.menuIsOpen ? -4 : 4,
    border: getDropdownIndicatorBorder(state),
    borderWidth: '0 2px 2px 0',
    display: 'inline-block',
    width: 8,
    height: 8,
    transition: 'all .2s ease',
    transform: !state.selectProps.menuIsOpen ? 'rotate(45deg)' : 'rotate(-135deg)',
  }),
  menu: (base, state) => ({
    ...base,
    marginTop: 0,
    marginBottom: 0,
    backgroundColor: 'var(--surface-neutral-tile)',
    border: '1px solid var(--border-neutral-secondary)',
    borderRadius: (state.placement === 'top') ? '3px 3px 0 0' : '0 0 3px 3px',
    borderTop: (state.placement !== 'top') ? 0 : '1px solid var(--border-neutral-secondary)',
    boxShadow: '0 0 0 1px hsl(0deg 0% 0% / 10%), 0 1px 5px hsl(0deg 0% 0% / 2%)',
    zIndex: 4,
  }),
  menuList: (base) => ({
    ...base,
    textAlign: 'left',
    padding: 0,
  }),
};

export const customPaginatorStyles = {
  control: (base, state) => (
    {
      ...base,
      height: 30,
      minHeight: 30,
      lineHeight: '28px',
      background: 'var(--surface-neutral-tile)',
      borderWidth: 1,
      zIndex: 1,
      borderRadius: state.menuIsOpen ? '0 0 3px 3px' : 3,
      borderColor: state.isFocused ? 'var(--border-neutral-secondary)' : 'var(--border-neutral-default)',
      boxShadow: state.isFocused ? '0 1px 5px rgba(0, 0, 0, 0.1)' : null,
      '&:hover': {
        borderColor: 'var(--border-neutral-secondary)',
      },
    }),
  input: (base) => ({
    ...base,
    margin: '0px',
    '> input': {
      height: 'auto',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  option: (base) => ({
    ...base,
    color: 'var(--text-neutral-default)',
    cursor: 'pointer',
    padding: '8px 10px',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: 3,
    marginRight: 11,
    marginTop: !state.selectProps.menuIsOpen ? -4 : 4,
    border: !state.isFocused ? 'solid var(--icon-neutral-secondary)' : 'solid var(--border-neutral-secondary)',
    borderWidth: '0 2px 2px 0',
    display: 'inline-block',
    width: 8,
    height: 8,
    transition: 'all .2s ease',
    transform: !state.selectProps.menuIsOpen ? 'rotate(45deg)' : 'rotate(-135deg)',
  }),
  menu: (base, state) => ({
    ...base,
    marginTop: 0,
    marginBottom: 0,
    border: '1px solid var(--border-neutral-secondary)',
    borderRadius: (state.placement === 'top') ? '3px 3px 0 0' : '0 0 3px 3px',
    borderBottom: (state.placement === 'top') ? 0 : '1px solid var(--border-neutral-secondary)',
    boxShadow: '0 0 0 1px hsl(0deg 0% 0% / 10%), 0 1px 5px hsl(0deg 0% 0% / 2%)',
    zIndex: 4,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 0 0 10px',
    whiteSpace: 'nowrap',
    alignItems: 'center',
    overflow: 'initial',
    textAlign: 'left',
  }),
  singleValue: (base) => ({
    ...base,
    marginLeft: 0,
    marginRight: 0,
  }),
  menuList: (base) => ({
    ...base,
    padding: 0,
  }),
};

export const customMobileStyles = {
  control: (base, state) => (
    {
      ...customStyles.control(base, state),
      '> div > div': {
        pointerEvents: 'none',
      },
    }
  ),
};
